import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersKommetjiePage() {
  const [activeTab, setActiveTab] = useState('overview');

  useSEO({
    title: 'Kommetjie Painters | Professional Painting Services | 20+ Years Experience',
    description: 'Expert painters in Kommetjie offering premium residential and commercial painting services. Specializing in coastal properties, beach houses, and seaside homes. Free quotes, quality workmanship, and professional service guaranteed.',
    keywords: 'Kommetjie painters, painters Kommetjie, painting services Kommetjie, house painters Kommetjie, residential painters, commercial painters, coastal property painters, beach house painters, professional painters',
    ogImage: 'https://public.readdy.ai/ai/img_res/3d84f4a94ec9a80d5d36d12448f7b556.jpg',
    schema: [
      generateLocalBusinessSchema({
        name: 'Painters Kommetjie',
        description: 'Professional painting services in Kommetjie specializing in coastal residential and commercial properties with over 20 years of experience.',
        address: {
          streetAddress: 'Kommetjie',
          addressLocality: 'Cape Town',
          addressRegion: 'Western Cape',
          postalCode: '7976',
          addressCountry: 'ZA'
        },
        geo: {
          latitude: '-34.1389',
          longitude: '18.3289'
        },
        areaServed: ['Kommetjie', 'Noordhoek', 'Ocean View', 'Scarborough', 'Misty Cliffs', 'Sun Valley', 'Fish Hoek'],
        priceRange: '$$-$$$',
        image: 'https://readdy.ai/api/search-image?query=professional%20painting%20company%20team%20working%20on%20coastal%20beach%20property%20modern%20equipment%20clean%20white%20background%20high%20quality%20professional%20photography&width=800&height=600&seq=kommetjie-business&orientation=landscape'
      }),
      generateServiceSchema({
        name: 'Coastal Property Painting Services',
        description: 'Premium painting services for coastal homes and beach properties in Kommetjie',
        provider: 'Painters Kommetjie',
        areaServed: 'Kommetjie, Cape Town',
        serviceType: 'Painting Service'
      })
    ]
  });

  const services = [
    {
      title: 'Coastal Property Painting',
      description: 'Specialized painting services for coastal homes using marine-grade weather-resistant coatings.',
      icon: 'ri-ship-line'
    },
    {
      title: 'Beach House Painting',
      description: 'Expert painting for beach houses and holiday homes with durable, salt-resistant finishes.',
      icon: 'ri-home-5-line'
    },
    {
      title: 'Interior Painting',
      description: 'Professional interior painting with coastal-inspired colors and premium quality finishes.',
      icon: 'ri-paint-brush-line'
    },
    {
      title: 'Exterior Weatherproofing',
      description: 'Durable exterior painting with advanced weatherproofing for harsh coastal conditions.',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Commercial Properties',
      description: 'Professional painting services for restaurants, shops, and commercial buildings.',
      icon: 'ri-store-2-line'
    },
    {
      title: 'Deck & Woodwork',
      description: 'Specialized painting and staining for decks, balconies, and outdoor wooden structures.',
      icon: 'ri-hammer-line'
    }
  ];

  const reviews = [
    {
      name: 'Mark Henderson',
      rating: 5,
      text: 'Outstanding work on our beach house! The team understood the challenges of coastal painting and used the right materials. Our home looks amazing and is well-protected from the ocean elements.',
      date: '2 weeks ago'
    },
    {
      name: 'Claire Daniels',
      rating: 5,
      text: 'We hired them for our holiday home in Kommetjie and couldn\'t be happier. Professional, efficient, and the quality is exceptional. They really know coastal properties!',
      date: '1 month ago'
    },
    {
      name: 'Robert van der Walt',
      rating: 5,
      text: 'Best painters in the area! They painted our restaurant and the results are stunning. Professional team that delivers on their promises. Highly recommended!',
      date: '1 month ago'
    },
    {
      name: 'Jessica Moore',
      rating: 5,
      text: 'Excellent service from start to finish. They painted both interior and exterior of our coastal home and handled everything professionally. Great attention to detail.',
      date: '2 months ago'
    },
    {
      name: 'Andrew Botha',
      rating: 5,
      text: 'Transformed our beachfront property beautifully! The painters were respectful, clean, and delivered exceptional quality. Their coastal expertise really shows.',
      date: '2 months ago'
    },
    {
      name: 'Sarah Williams',
      rating: 5,
      text: 'Professional team that takes pride in their work. They completed our project on time and the finish is perfect. Worth every cent for the quality received.',
      date: '3 months ago'
    }
  ];

  const galleryImages = [
    {
      url: 'https://readdy.ai/api/search-image?query=coastal%20beach%20house%20exterior%20freshly%20painted%20white%20and%20blue%20ocean%20views%20clean%20simple%20background%20professional%20photography%20bright%20daylight%20seaside%20property&width=600&height=400&seq=kommetjie-gallery-1&orientation=landscape',
      title: 'Beach House Exterior'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=coastal%20home%20interior%20living%20room%20freshly%20painted%20light%20colors%20ocean%20inspired%20decor%20clean%20simple%20background%20professional%20photography%20natural%20lighting&width=600&height=400&seq=kommetjie-gallery-2&orientation=landscape',
      title: 'Coastal Interior'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=modern%20beach%20property%20exterior%20painted%20white%20contemporary%20coastal%20architecture%20clean%20simple%20background%20professional%20photography%20bright%20natural%20light&width=600&height=400&seq=kommetjie-gallery-3&orientation=landscape',
      title: 'Modern Beach Property'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=seaside%20restaurant%20interior%20freshly%20painted%20coastal%20theme%20dining%20area%20clean%20simple%20background%20professional%20photography%20bright%20natural%20light&width=600&height=400&seq=kommetjie-gallery-4&orientation=landscape',
      title: 'Seaside Restaurant'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=wooden%20deck%20freshly%20stained%20ocean%20views%20outdoor%20seating%20clean%20simple%20background%20professional%20photography%20bright%20daylight&width=600&height=400&seq=kommetjie-gallery-5&orientation=landscape',
      title: 'Deck Restoration'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=holiday%20home%20exterior%20painted%20coastal%20colors%20beach%20setting%20clean%20simple%20background%20professional%20photography%20natural%20lighting&width=600&height=400&seq=kommetjie-gallery-6&orientation=landscape',
      title: 'Holiday Home'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-bold" style={{ color: '#e9f638' }}>
              Painters Directory
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-gray-300 transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-gray-300 transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-gray-300 transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="px-6 py-2 rounded-lg whitespace-nowrap transition-colors" style={{ backgroundColor: '#e9f638', color: '#000' }}>
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=kommetjie%20beach%20cape%20town%20coastal%20village%20ocean%20views%20lighthouse%20clean%20simple%20background%20professional%20photography%20bright%20natural%20lighting%20seaside%20community&width=1920&height=600&seq=kommetjie-hero&orientation=landscape"
            alt="Kommetjie Painters"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div className="w-full">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Painters Kommetjie</h1>
            <p className="text-xl md:text-2xl text-white mb-6">Expert Painting Services for Coastal Properties</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/get-a-quote"
                className="px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
                style={{ backgroundColor: '#e9f638', color: '#000' }}
              >
                Get Free Quote
              </Link>
              <a
                href="tel:0219876543"
                className="px-8 py-3 bg-white text-black rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 sticky top-16 bg-white z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {['overview', 'services', 'gallery', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'overview' && (
          <div className="space-y-12">
            {/* About Section */}
            <section>
              <h2 className="text-3xl font-bold mb-6">About Painters Kommetjie</h2>
              <div className="prose max-w-none space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Welcome to Painters Kommetjie, your premier choice for professional painting services in one of Cape Town's most beautiful and unique coastal villages. With over 20 years of experience in the painting industry, we have established ourselves as the leading painting contractor for coastal properties, beach houses, and commercial establishments in the Kommetjie area and surrounding Southern Peninsula communities.
                </p>
                <p>
                  Kommetjie is a special place, known for its stunning beaches, dramatic ocean views, iconic lighthouse, and laid-back coastal lifestyle. Our team of expert painters understands the unique challenges and requirements of painting in this exposed coastal environment, from dealing with strong winds and salt spray to protecting properties from the harsh Atlantic weather. We take pride in delivering exceptional results that enhance the beauty of your property while providing superior protection against the demanding coastal elements.
                </p>
                <p>
                  Our company was founded on the principles of quality craftsmanship, specialized coastal expertise, and unwavering commitment to customer satisfaction. Over the years, we have built a reputation for excellence by consistently delivering outstanding results on every project, whether it's a beachfront home renovation, holiday house refresh, coastal cottage restoration, or commercial property upgrade. We understand that your coastal property is a valuable investment and a special place, and we treat every project with the care, respect, and expertise it deserves.
                </p>
                <p>
                  What sets Painters Kommetjie apart from other painting contractors is our specialized expertise in coastal property painting, our commitment to using marine-grade and weather-resistant materials specifically designed for harsh coastal conditions, and our team of highly skilled painters who are experienced in working with properties exposed to ocean elements. We work closely with homeowners, property managers, holiday home owners, and business owners to ensure that every aspect of the painting project meets or exceeds expectations while providing long-lasting protection and beautiful results.
                </p>
                <p>
                  Our expertise extends across all types of coastal painting projects, including interior painting, exterior painting, weatherproofing, deck and woodwork restoration, specialty coatings, and commercial painting. We are particularly experienced in working with properties that face the full force of Atlantic weather, requiring specialized preparation techniques, premium coatings, and expert application methods. Whether you're looking to protect your beachfront home, refresh your holiday house, restore your coastal cottage, or maintain your commercial property, we have the skills, experience, and resources to deliver exceptional results that stand the test of time.
                </p>
                <p>
                  We understand that coastal properties require special attention and care during painting projects. That's why we use advanced preparation techniques, marine-grade primers, weather-resistant topcoats, and proper application methods to ensure maximum durability and protection. Our team is fully insured, professionally trained in coastal painting techniques, and committed to maintaining a clean and safe work environment. We use dust-free sanding equipment, protective coverings, and environmentally responsible materials to ensure that your property and the surrounding coastal environment remain protected throughout the painting process.
                </p>
                <p>
                  At Painters Kommetjie, we believe in building long-term relationships with our clients and being an active part of this special coastal community. Many of our clients are repeat customers who return to us for all their painting needs and recommend us to their neighbors, friends, and fellow property owners. This word-of-mouth reputation within the Kommetjie community is something we value highly and work hard to maintain with every project we undertake. We're proud to contribute to maintaining and enhancing the beauty of Kommetjie's unique coastal properties.
                </p>
                <p>
                  Our service area includes Kommetjie, Noordhoek, Ocean View, Scarborough, Misty Cliffs, Sun Valley, Fish Hoek, and surrounding Southern Peninsula areas. We are intimately familiar with the local coastal conditions, architectural styles, and community character, which allows us to provide expert advice and recommendations tailored to your specific property and location. Whether you own a beachfront home, holiday house, coastal cottage, or commercial property, we have the specialized expertise to deliver results that exceed your expectations while providing superior protection against the demanding coastal environment.
                </p>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-3xl font-bold mb-6">Why Choose Painters Kommetjie?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-medal-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">20+ Years Experience</h3>
                    <p className="text-gray-600">Extensive experience in coastal property painting throughout Kommetjie and the Southern Peninsula.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-ship-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Coastal Specialists</h3>
                    <p className="text-gray-600">Specialized expertise in coastal painting using marine-grade materials and advanced weatherproofing techniques.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-star-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Premium Materials</h3>
                    <p className="text-gray-600">We use only the finest marine-grade and weather-resistant paints designed for harsh coastal conditions.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Fully Insured</h3>
                    <p className="text-gray-600">Comprehensive insurance coverage for your peace of mind and property protection.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-leaf-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Eco-Friendly</h3>
                    <p className="text-gray-600">Environmentally responsible practices and materials to protect our beautiful coastal environment.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-community-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Local Community</h3>
                    <p className="text-gray-600">Proud members of the Kommetjie community committed to maintaining its unique coastal character.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Service Areas */}
            <section>
              <h2 className="text-3xl font-bold mb-6">Service Areas</h2>
              <p className="text-gray-700 mb-4">
                We proudly serve Kommetjie and surrounding Southern Peninsula areas including:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Kommetjie', 'Noordhoek', 'Ocean View', 'Scarborough', 'Misty Cliffs', 'Sun Valley', 'Fish Hoek', 'Simons Town'].map((area) => (
                  <div key={area} className="flex items-center space-x-2">
                    <i className="ri-map-pin-line" style={{ color: '#e9f638' }}></i>
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6">Our Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg mb-4" style={{ backgroundColor: '#e9f638' }}>
                      <i className={`${service.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Detailed Services */}
            <section className="prose max-w-none">
              <h2 className="text-3xl font-bold mb-6">Comprehensive Painting Solutions</h2>
              <div className="space-y-6 text-gray-700">
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Coastal Property Painting</h3>
                  <p>
                    Our coastal property painting services are specifically designed for homes and buildings in Kommetjie's challenging Atlantic coastal environment. We understand the unique demands placed on coastal properties by salt spray, strong winds, intense sun, and moisture. Our specialized approach includes thorough surface preparation, salt removal and neutralization, marine-grade primers, weather-resistant topcoats, and advanced application techniques. We use only premium quality paints and coatings specifically formulated for harsh coastal conditions, ensuring your property is beautifully protected for years to come. Our coastal painting services also include ongoing maintenance programs to keep your property looking pristine and well-protected.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Beach House Painting</h3>
                  <p>
                    Beach houses and holiday homes require special attention to maintain their beauty and protect them from the elements. Our beach house painting services combine aesthetic excellence with superior protection, using durable, salt-resistant finishes that withstand the demanding coastal environment. We specialize in creating beautiful coastal color schemes that complement the natural surroundings while providing maximum durability. Whether you're looking for classic beach house whites and blues, contemporary coastal colors, or natural weathered finishes, we have the expertise to deliver stunning results that enhance your beach house's character while providing long-lasting protection against ocean elements.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Exterior Weatherproofing</h3>
                  <p>
                    Kommetjie's exposed coastal location demands superior weatherproofing to protect your property investment. Our exterior weatherproofing services provide comprehensive protection using advanced coating systems, waterproofing membranes, and specialized application techniques. We address all vulnerable areas including walls, roofs, decks, balconies, and woodwork, ensuring complete protection from wind-driven rain, salt spray, and moisture penetration. Our weatherproofing approach includes thorough inspection, crack repair, surface preparation, primer application, and multiple coats of premium weather-resistant paint designed specifically for harsh coastal conditions. The result is a beautiful, durable finish that protects your property for years to come.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Deck and Woodwork Restoration</h3>
                  <p>
                    Wooden decks, balconies, and outdoor structures face particular challenges in coastal environments. Our deck and woodwork restoration services specialize in protecting and beautifying these important outdoor living spaces. We use premium wood stains, sealers, and protective coatings designed for coastal exposure, ensuring your wooden structures remain beautiful and structurally sound. Our restoration process includes thorough cleaning, sanding, repair of damaged areas, application of wood preservatives, and multiple coats of marine-grade finishes. Whether you have a large entertaining deck, intimate balcony, or outdoor wooden structures, we have the expertise to restore and protect them against the demanding coastal environment.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Work</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, index) => (
                <div key={index} className="group relative overflow-hidden rounded-lg aspect-video">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover object-top transition-transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <p className="text-white font-semibold text-lg">{image.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Customer Reviews</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((review, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{review.name}</h3>
                      <p className="text-sm text-gray-500">{review.date}</p>
                    </div>
                    <div className="flex space-x-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill" style={{ color: '#e9f638' }}></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: '#e9f638' }}>
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Coastal Property?</h2>
          <p className="text-xl mb-8">Get a free, no-obligation quote for your painting project today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/get-a-quote"
              className="px-8 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              Request Free Quote
            </Link>
            <a
              href="tel:0219876543"
              className="px-8 py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#e9f638' }}>Painters Kommetjie</h3>
              <p className="text-gray-400">Professional painting services for coastal properties in Kommetjie and surrounding areas.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/get-a-quote" className="text-gray-400 hover:text-white transition-colors">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Coastal Property Painting</li>
                <li>Beach House Painting</li>
                <li>Exterior Weatherproofing</li>
                <li>Deck &amp; Woodwork</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line"></i>
                  <span>021 987 6543</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Kommetjie, Cape Town</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Painters Kommetjie. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="https://leaderr.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Powered by leaderr
              </a>
              <a href="https://seostudio.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}